/**
 * Turning a prompt version's model block into a playground selection.
 *
 * These are two different kinds of object and the difference is the whole point
 * of this file:
 *
 *   A prompt's `model` is OPEN JSON. Trodo never calls anything with it — the
 *   author writes it for their own codebase, so it can name a provider we have
 *   no key for, a model that doesn't exist here, a param called `temp` instead
 *   of `temperature`, or a note to a colleague. Nothing validates it, by design
 *   (see backend/services/prompts/validate.js).
 *
 *   A playground panel's `model` is a REQUEST WE ARE ABOUT TO MAKE. It has to
 *   name a credential this team actually holds and a model that credential can
 *   actually reach, because the next thing that happens is a real call that
 *   costs real money.
 *
 * So importing a prompt into the playground is a convenience, not a handover:
 * we adopt each field only when it survives the crossing, and leave the panel's
 * own selection alone when it doesn't. Prefilling `provider: 'someguy'` would
 * put a run in front of the user that cannot succeed, and blame the prompt for
 * it.
 */

const isObj = (v) => v != null && typeof v === 'object' && !Array.isArray(v);

/** A number the panel's controls can actually represent, or undefined. */
const inRange = (v, min, max) => (
  typeof v === 'number' && Number.isFinite(v) && v >= min && v <= max ? v : undefined
);

/**
 * @param {object}   versionModel  the prompt version's `model` block (untrusted)
 * @param {object}   opts
 * @param {array}    opts.integrations         credentials this team holds
 * @param {function} opts.modelsForCredential  credential → reachable model ids
 * @param {object}   opts.current              the panel's model selection today
 * @returns {object} the panel's next model selection
 */
export function modelFromPromptVersion(versionModel, { integrations, modelsForCredential, current }) {
  const vm = isObj(versionModel) ? versionModel : {};
  const now = isObj(current) ? current : {};
  const creds = Array.isArray(integrations) ? integrations : [];

  // A credential we actually hold: the one the version names if it's ours,
  // otherwise any key for the provider it names.
  const named = creds.find((c) => String(c.id) === String(vm.credential_id));
  const byProvider = vm.provider ? creds.find((c) => c.provider === vm.provider) : null;
  const cred = named || byProvider || null;

  // No key for what the version asks for → import the CONTENT and leave the
  // model selection untouched. The messages are what the user came for; the
  // model is a choice they still have to make.
  if (!cred) return now;

  const reachable = (modelsForCredential?.(cred) || []).map(String);
  const adoptable = vm.model && reachable.includes(String(vm.model));

  return {
    ...now,
    credential_id: cred.id,
    provider: cred.provider,
    // The version's model only if this key can reach it. Otherwise keep what
    // the panel already had (when that's still reachable), and fall back to the
    // credential's first model just so the dropdown isn't empty.
    model: adoptable
      ? vm.model
      : (reachable.includes(String(now.model)) ? now.model : (reachable[0] || '')),
    // Params only when they're numbers the panel's own controls can show. A
    // prompt may legitimately carry `temperature: 5` for a gateway that allows
    // it; the slider here tops out at 2, and silently clamping would misreport
    // what's about to be sent.
    temperature: inRange(vm.temperature, 0, 2) ?? inRange(now.temperature, 0, 2) ?? 0.2,
    max_tokens: inRange(vm.max_tokens, 1, Number.MAX_SAFE_INTEGER) ?? now.max_tokens ?? '',
  };
}

export default modelFromPromptVersion;
